import * as THREE from 'three';
import {
  buildAnchorKey,
  findModuleGroup,
  getLocalFrameBox,
  localToRel,
} from './AnchorResolver';
import { getPartBox } from './PartResolver';
import { projectToScreen } from './ProjectionEngine';

/**
 * Bán kính bắt dính (px) theo loại điểm. Góc dễ bắt nhất, điểm bất kỳ trên cạnh
 * phải gần hơn mới bắt để không lấn át góc / trung điểm.
 */
export const SNAP_RADIUS_PX = 18;
const RADIUS_BY_KIND = {
  corner: SNAP_RADIUS_PX,
  edge: 14,
  face: 12,
  center: 12,
  onEdge: 9,
};
/**
 * Điểm cộng thêm (px) khi so sánh: góc > giữa cạnh > tâm mặt > tâm khối > trên cạnh.
 * "Trên cạnh" cộng bằng bán kính góc (18) nên chỉ thắng khi KHÔNG có góc / trung
 * điểm / tâm mặt nào trong tầm — đi dọc cạnh về phía góc vẫn bắt được góc.
 */
const KIND_BIAS_PX = { corner: 0, edge: 3, face: 5, center: 8, onEdge: 18 };

/** Góc nhìn tối thiểu (độ) giữa hai mặt kề nhau để coi cạnh chung là cạnh thật. */
const FEATURE_EDGE_ANGLE_DEG = 25;
/** Mesh quá nặng: chỉ dùng 8 góc bbox của nó thay vì trích cạnh. */
const MAX_VERTICES_FOR_EDGES = 60000;
/** Số ứng viên tốt nhất được kiểm tra che khuất (mỗi cái là một lần raycast). */
const MAX_VISIBILITY_CHECKS = 8;
/** Điểm của mesh trùng điểm khung trong khoảng này (m) thì lưu theo key của khung. */
const FRAME_MATCH_M = 0.004;
/** Mở rộng vùng màn hình của module (px) vì cánh / mặt đá thò ra ngoài khung. */
const GROUP_RECT_MARGIN_PX = 80;

const SIDES = ['min', 'c', 'max'];
const FRAME_KINDS = ['corner', 'edge', 'face', 'center'];

/** 27 điểm kỹ thuật của một bbox khung (key + toạ độ local + loại). */
export const snapPointsOfBox = box => {
  const points = [];
  const center = box.getCenter(new THREE.Vector3());
  SIDES.forEach(x =>
    SIDES.forEach(y =>
      SIDES.forEach(z => {
        const sides = { x, y, z };
        const local = new THREE.Vector3();
        ['x', 'y', 'z'].forEach(axis => {
          local[axis] =
            sides[axis] === 'min'
              ? box.min[axis]
              : sides[axis] === 'max'
                ? box.max[axis]
                : center[axis];
        });
        const centers = [x, y, z].filter(v => v === 'c').length;
        points.push({
          key: buildAnchorKey(sides),
          local,
          kind: FRAME_KINDS[centers],
        });
      })
    )
  );
  return points;
};

const isVisibleDeep = object => {
  for (let o = object; o; o = o.parent) {
    if (o.visible === false) return false;
  }
  return true;
};

// ---- cạnh đặc trưng của mesh -------------------------------------------

const edgeCache = new WeakMap();

/**
 * Các cạnh "thật" của geometry (cạnh biên hoặc giữa hai mặt lệch nhau >= 25°),
 * dạng mảng phẳng [ax,ay,az, bx,by,bz, ...] trong hệ local của mesh.
 * Đường chéo giữa hai tam giác cùng mặt phẳng bị loại. Kết quả được cache.
 */
export const featureEdgesOf = geometry => {
  if (!geometry?.attributes?.position) return null;
  if (edgeCache.has(geometry)) return edgeCache.get(geometry);
  let edges;
  if (geometry.attributes.position.count > MAX_VERTICES_FOR_EDGES) {
    edges = bboxEdges(geometry);
  } else {
    const edgesGeometry = new THREE.EdgesGeometry(
      geometry,
      FEATURE_EDGE_ANGLE_DEG
    );
    edges = Float32Array.from(edgesGeometry.attributes.position.array);
    edgesGeometry.dispose();
  }
  edgeCache.set(geometry, edges);
  return edges;
};

/** 12 cạnh của bbox geometry (dùng cho mesh quá nặng). */
const bboxEdges = geometry => {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox;
  const c = (i, j, k) => [
    i ? max.x : min.x,
    j ? max.y : min.y,
    k ? max.z : min.z,
  ];
  const out = [];
  [0, 1].forEach(i =>
    [0, 1].forEach(j => {
      out.push(...c(i, j, 0), ...c(i, j, 1));
      out.push(...c(i, 0, j), ...c(i, 1, j));
      out.push(...c(0, i, j), ...c(1, i, j));
    })
  );
  return Float32Array.from(out);
};

// ---- tiện ích ----------------------------------------------------------

/** Hình chữ nhật trên màn hình của bbox (local) sau transform; null nếu sau camera. */
const screenRectOfBox = (box, matrixWorld, camera, width, height) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const v = new THREE.Vector3();
  for (const x of [box.min.x, box.max.x]) {
    for (const y of [box.min.y, box.max.y]) {
      for (const z of [box.min.z, box.max.z]) {
        v.set(x, y, z).applyMatrix4(matrixWorld);
        const s = projectToScreen(v, camera, width, height);
        if (!s.visible) return null;
        minX = Math.min(minX, s.x);
        maxX = Math.max(maxX, s.x);
        minY = Math.min(minY, s.y);
        maxY = Math.max(maxY, s.y);
      }
    }
  }
  return { minX, minY, maxX, maxY };
};

const rectNear = (rect, x, y, margin) =>
  rect &&
  x >= rect.minX - margin &&
  x <= rect.maxX + margin &&
  y >= rect.minY - margin &&
  y <= rect.maxY + margin;

/**
 * Điểm có nhìn thấy từ camera không (không bị mesh nào che).
 * Dung sai theo khoảng cách để điểm nằm ngay trên bề mặt vẫn được tính là thấy.
 */
const isPointVisible = (world, camera, raycaster, targets) => {
  const origin = new THREE.Vector3();
  const dir = new THREE.Vector3();
  if (camera.isOrthographicCamera) {
    camera.getWorldDirection(dir);
    origin.copy(world).addScaledVector(dir, -1000);
  } else {
    origin.setFromMatrixPosition(camera.matrixWorld);
    dir.subVectors(world, origin);
  }
  const distance = dir.length();
  if (distance < 1e-6) return true;
  dir.divideScalar(distance);
  raycaster.set(origin, dir);
  raycaster.near = 0;
  raycaster.far = distance;
  const tolerance = Math.max(0.006, distance * 0.004);
  const hit = raycaster
    .intersectObjects(targets, true)
    .find(h => h.object.isMesh && isVisibleDeep(h.object));
  raycaster.far = Infinity;
  return !hit || hit.distance >= distance - tolerance;
};

/** Toạ độ world của điểm trên đoạn AB ứng với tham số màn hình u (đúng phối cảnh). */
const perspectiveLerp = (a, b, sa, sb, u) => {
  const da = sa.depth ?? 1;
  const db = sb.depth ?? 1;
  const denom = db * (1 - u) + da * u;
  const t = denom > 1e-9 ? (u * da) / denom : u;
  return a.clone().lerp(b, t);
};

// ---- tìm điểm ----------------------------------------------------------

/**
 * Tìm điểm bắt dính gần con trỏ nhất trên các module.
 *
 * Ứng viên (đều phải NHÌN THẤY được, không bị vật khác che):
 *  - đỉnh, trung điểm, điểm bất kỳ trên các cạnh thật của mesh (cánh, hông,
 *    mặt đá, chân…) — đúng những góc người dùng nhìn thấy
 *  - 27 điểm kỹ thuật của bbox khung (tâm mặt hữu ích khi không có cạnh)
 * Nếu điểm của mesh trùng góc / trung điểm của khung, anchor lưu theo key của
 * khung (bám chính xác khi đổi kích thước). Ngược lại lưu theo toạ độ tương đối
 * của khung (`rel`). Không có ứng viên: lấy điểm trên bề mặt dưới con trỏ.
 *
 * @param {{x:number,y:number,camera:THREE.Camera,width:number,height:number,
 *          groups:{instanceId:string,group:THREE.Group}[],
 *          parts?:{instanceId:string,part:string,group:THREE.Group,
 *                  meshes:THREE.Mesh[]}[],
 *          radiusPx?:number, raycaster?:THREE.Raycaster, frameOpts?:object}} args
 *   radiusPx: giới hạn bán kính (mặc định theo loại điểm)
 * @returns {null | {anchor:object, world:THREE.Vector3, kind:string,
 *                   screen:{x:number,y:number}}}
 *   kind: corner | edge | onEdge | face | center | surface
 */
export const findSnap = ({
  x,
  y,
  camera,
  width,
  height,
  groups,
  parts = [],
  radiusPx,
  raycaster,
  frameOpts,
}) => {
  if (!camera || !(groups?.length || parts?.length)) return null;
  camera.updateMatrixWorld?.();
  const caster = raycaster ?? new THREE.Raycaster();
  const radiusOf = kind => Math.min(RADIUS_BY_KIND[kind], radiusPx ?? Infinity);
  const maxRadius = radiusOf('corner');
  // Mặt đá / ốp tường là mesh cấp scene: cũng là vật che và đối tượng bắt dính
  const targets = [
    ...groups.map(g => g.group),
    ...parts.flatMap(p => p.meshes),
  ];
  const candidates = [];
  const push = (kind, world, screen, entry, extra = {}) => {
    const distance = Math.hypot(screen.x - x, screen.y - y);
    if (distance > radiusOf(kind)) return;
    candidates.push({
      kind,
      world,
      screen,
      entry,
      score: distance + KIND_BIAS_PX[kind],
      ...extra,
    });
  };

  const frames = new Map();
  [...groups, ...parts].forEach(entry => {
    const { group } = entry;
    group.updateWorldMatrix(true, false);
    const box = entry.part
      ? getPartBox(group, entry.meshes)
      : getLocalFrameBox(group, frameOpts);
    frames.set(entry, box);
    if (box) {
      const rect = screenRectOfBox(
        box,
        group.matrixWorld,
        camera,
        width,
        height
      );
      // Module ở xa con trỏ: bỏ qua cả module
      if (rect && !rectNear(rect, x, y, GROUP_RECT_MARGIN_PX)) return;
      snapPointsOfBox(box).forEach(point => {
        const world = point.local.clone().applyMatrix4(group.matrixWorld);
        const screen = projectToScreen(world, camera, width, height);
        if (screen.visible) {
          push(point.kind, world, screen, entry, {
            key: point.key,
            frame: true,
          });
        }
      });
    }

    // Cạnh thật của các mesh nằm gần con trỏ (bộ phận: chỉ các mesh của nó)
    const eachMesh = fn =>
      entry.part ? entry.meshes.forEach(fn) : group.traverse(fn);
    eachMesh(object => {
      if (!object.isMesh || !object.geometry || !isVisibleDeep(object)) return;
      object.updateWorldMatrix(true, false);
      const geometry = object.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const rect = screenRectOfBox(
        geometry.boundingBox,
        object.matrixWorld,
        camera,
        width,
        height
      );
      if (!rectNear(rect, x, y, maxRadius)) return;
      const edges = featureEdgesOf(geometry);
      if (!edges) return;
      const a = new THREE.Vector3();
      const b = new THREE.Vector3();
      for (let i = 0; i + 5 < edges.length; i += 6) {
        a.set(edges[i], edges[i + 1], edges[i + 2]).applyMatrix4(
          object.matrixWorld
        );
        b.set(edges[i + 3], edges[i + 4], edges[i + 5]).applyMatrix4(
          object.matrixWorld
        );
        const sa = projectToScreen(a, camera, width, height);
        const sb = projectToScreen(b, camera, width, height);
        if (!sa.visible || !sb.visible) continue;
        push('corner', a.clone(), sa, entry);
        push('corner', b.clone(), sb, entry);
        const mid = a.clone().add(b).multiplyScalar(0.5);
        push('edge', mid, projectToScreen(mid, camera, width, height), entry);
        // Điểm trên cạnh gần con trỏ nhất (trên màn hình)
        const ex = sb.x - sa.x;
        const ey = sb.y - sa.y;
        const len2 = ex * ex + ey * ey;
        if (len2 > 1) {
          const u = Math.max(
            0,
            Math.min(1, ((x - sa.x) * ex + (y - sa.y) * ey) / len2)
          );
          if (u > 0.02 && u < 0.98) {
            push(
              'onEdge',
              perspectiveLerp(a, b, sa, sb, u),
              { x: sa.x + ex * u, y: sa.y + ey * u },
              entry
            );
          }
        }
      }
    });
  });

  candidates.sort((p, q) => p.score - q.score);
  let best = null;
  let checks = 0;
  const seen = [];
  for (const c of candidates) {
    if (checks >= MAX_VISIBILITY_CHECKS) break;
    // Bỏ trùng (cùng một điểm được sinh ra từ nhiều cạnh)
    if (seen.some(w => w.distanceToSquared(c.world) < 1e-8)) continue;
    seen.push(c.world);
    checks += 1;
    if (isPointVisible(c.world, camera, caster, targets)) {
      best = c;
      break;
    }
  }

  if (best) return toSnap(best, frames.get(best.entry));

  // Không có điểm kỹ thuật gần: lấy điểm trên bề mặt dưới con trỏ
  caster.setFromCamera(
    new THREE.Vector2((x / width) * 2 - 1, -(y / height) * 2 + 1),
    camera
  );
  const byId = new Map(groups.map(g => [g.instanceId, g]));
  const partByMesh = new Map();
  parts.forEach(p => p.meshes.forEach(m => partByMesh.set(m, p)));
  const hit = caster
    .intersectObjects(targets, true)
    .find(h => h.object.isMesh && isVisibleDeep(h.object));
  if (!hit) return null;
  let entry = partByMesh.get(hit.object);
  if (!entry) {
    const hitGroup = findModuleGroup(hit.object);
    entry = byId.get(hitGroup?.userData?.instanceId);
    if (!entry || entry.group !== hitGroup) return null;
  }
  return toSnap(
    { kind: 'surface', world: hit.point.clone(), screen: { x, y }, entry },
    frames.get(entry)
  );
};

/** Ứng viên -> kết quả, chọn dạng anchor phù hợp. */
const toSnap = (candidate, box) => {
  const { entry, world, kind, screen } = candidate;
  const { instanceId, group } = entry;
  // Anchor của bộ phận mang thêm `part` để tra lại đúng mesh sau khi tạo lại
  const withPart = a => (entry.part ? { ...a, part: entry.part } : a);
  let anchor;
  if (candidate.key) {
    anchor = { instanceId, key: candidate.key };
  } else {
    // Trùng điểm của khung (cùng loại) thì dùng key cho bền khi đổi kích thước
    const match =
      box && (kind === 'corner' || kind === 'edge')
        ? snapPointsOfBox(box).find(
            p =>
              p.kind === kind &&
              p.local
                .clone()
                .applyMatrix4(group.matrixWorld)
                .distanceTo(world) < FRAME_MATCH_M
          )
        : null;
    if (match) {
      anchor = { instanceId, key: match.key };
    } else {
      group.updateWorldMatrix(true, false);
      const local = group.worldToLocal(world.clone());
      anchor = box
        ? { instanceId, rel: localToRel(box, local) }
        : { instanceId, local: local.toArray() };
    }
  }
  return {
    anchor: withPart(anchor),
    world,
    kind,
    screen: { x: screen.x, y: screen.y },
  };
};
